import React, { useState, useEffect, useCallback } from 'react';
import { getCurrentSchedule, getScheduleVersions, patchSection } from './fileService';
import { ZmanEnum, titleMapper } from '../calendar/zmanim';
import { useScheduleVersionCheck } from '../hooks/useScheduleVersionCheck';
import StaleVersionBar from './StaleVersionBar';

const KioskSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [displayOptionsVersion, setDisplayOptionsVersion] = useState(null);
  const versionMap = displayOptionsVersion !== null ? { displayOptions: displayOptionsVersion } : {};
  const { isStale } = useScheduleVersionCheck(versionMap, ['displayOptions']);
  const [displayOptions, setDisplayOptions] = useState({
    showZmanim: true,
    showDailyStudies: true,
    showServiceSchedule: true,
    showUpcomingSchedule: true,
    showSefirasHaomer: false,
    showThreeKapitels: false,
    showCommunityUpdates: true,
    showNotices: true,
    showDonation: true,
    showCustomHtml: false,
    selectedZmanim: [],
    selectedDailyStudies: [],
    showProgressBar: true,
    customHtmlContent: '',
  });

  const loadSettings = useCallback(async () => {
    try {
      setLoading(true);
      const [schedule, serverVersions] = await Promise.all([
        getCurrentSchedule(),
        getScheduleVersions(),
      ]);

      setDisplayOptionsVersion(serverVersions.displayOptions || null);

      const currentOptions = schedule.displayOptions || {};
      const allZmanimKeys = Object.values(ZmanEnum);

      setDisplayOptions({
        showZmanim: currentOptions.showZmanim !== false,
        showDailyStudies: currentOptions.showDailyStudies !== false,
        showServiceSchedule: currentOptions.showServiceSchedule !== false,
        showUpcomingSchedule: currentOptions.showUpcomingSchedule !== false,
        showSefirasHaomer: currentOptions.showSefirasHaomer === true,
        showThreeKapitels: currentOptions.showThreeKapitels === true,
        showCommunityUpdates: currentOptions.showCommunityUpdates !== false,
        showNotices: currentOptions.showNotices !== false,
        showDonation: currentOptions.showDonation !== false,
        showCustomHtml: currentOptions.showCustomHtml === true,
        selectedZmanim: currentOptions.selectedZmanim || allZmanimKeys,
        selectedDailyStudies: currentOptions.selectedDailyStudies || ['onePerek', 'threePerokim', 'tanya'],
        showProgressBar: currentOptions.showProgressBar !== false,
        customHtmlContent: currentOptions.customHtmlContent || '',
      });
    } catch (error) {
      console.error('Error loading kiosk settings:', error);
      setSaveError('Failed to load settings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const handleToggle = (key) => {
    setDisplayOptions(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
    setSaveSuccess(false);
    setSaveError('');
  };

  const handleCustomHtmlChange = (value) => {
    setDisplayOptions(prev => ({
      ...prev,
      customHtmlContent: value
    }));
    setSaveSuccess(false);
    setSaveError('');
  };

  const handleZmanimToggle = (zmanKey) => {
    setDisplayOptions(prev => {
      const current = prev.selectedZmanim || [];
      const updated = current.includes(zmanKey)
        ? current.filter(k => k !== zmanKey)
        : [...current, zmanKey];
      return {
        ...prev,
        selectedZmanim: updated
      };
    });
    setSaveSuccess(false);
    setSaveError('');
  };

  const handleDailyStudyToggle = (studyKey) => {
    setDisplayOptions(prev => {
      const current = prev.selectedDailyStudies || [];
      const updated = current.includes(studyKey)
        ? current.filter(k => k !== studyKey)
        : [...current, studyKey];
      return {
        ...prev,
        selectedDailyStudies: updated
      };
    });
    setSaveSuccess(false);
    setSaveError('');
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setSaveSuccess(false);
      setSaveError('');

      const result = await patchSection('displayOptions', displayOptions, displayOptionsVersion);
      setDisplayOptionsVersion(result.version);

      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (error) {
      if (error.conflict) {
        setSaveError('Settings were modified elsewhere. Click reload below to get the latest.');
      } else {
        console.error('Error saving kiosk settings:', error);
        setSaveError('Failed to save settings. Please try again.');
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <div style={{ fontSize: '18px', color: '#666' }}>Loading Kiosk Settings...</div>
      </div>
    );
  }

  const boxGroups = [
    {
      title: 'Left Panel',
      boxes: [
        { key: 'showZmanim', label: 'Zmanim (זמנים)', description: 'Prayer times display' },
        { key: 'showDailyStudies', label: 'Daily Studies', description: 'Rambam and Tanya studies' },
      ]
    },
    {
      title: 'Center Panel',
      boxes: [
        { key: 'showServiceSchedule', label: 'Service Schedule', description: 'Current service schedule' },
        { key: 'showUpcomingSchedule', label: 'Upcoming Schedule', description: 'Upcoming events schedule' },
      ]
    },
    {
      title: 'Right Panel',
      boxes: [
        { key: 'showSefirasHaomer', label: 'Sefiras Ha\'omer', description: 'Sefirah countdown (replaces Community Updates when shown)' },
        { key: 'showThreeKapitels', label: '3 Kapitels Tehillim', description: 'Elul–Yom Kippur chapters (shown only on those days; replaces Community Updates when shown)' },
        { key: 'showCommunityUpdates', label: 'Community Updates', description: 'News and community updates' },
        { key: 'showNotices', label: 'Notices', description: 'Weekly notices' },
        { key: 'showDonation', label: 'Donation Section', description: 'Support BLY QR codes' },
        { key: 'showCustomHtml', label: 'Custom HTML Box', description: 'Display custom HTML content' },
      ]
    }
  ];

  return (
    <div style={{ maxWidth: '800px', margin: '0 auto' }}>
        {isStale && <StaleVersionBar onReload={loadSettings} />}
        <h2>Kiosk Display Settings</h2>
        <p style={{ color: '#666', marginBottom: '30px' }}>
          Control which boxes are displayed on the kiosk page. Changes take effect immediately after saving.
        </p>

        {boxGroups.map((group, groupIndex) => (
          <div key={groupIndex} style={{ marginBottom: '40px' }}>
            <h3 style={{ 
              borderBottom: '2px solid #ddd', 
              paddingBottom: '10px',
              marginBottom: '20px',
              color: '#333'
            }}>
              {group.title}
            </h3>
            <div style={{ display: 'grid', gap: '15px' }}>
              {group.boxes.map((box) => (
                <div 
                  key={box.key}
                  style={{
                    padding: '15px',
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    backgroundColor: displayOptions[box.key] ? '#f0f9ff' : '#f9f9f9',
                    transition: 'background-color 0.2s'
                  }}
                >
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: displayOptions[box.key] && (
                      box.key === 'showZmanim' || 
                      box.key === 'showDailyStudies' || 
                      box.key === 'showCommunityUpdates'
                    ) ? '15px' : '0'
                  }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ 
                        fontWeight: 'bold', 
                        marginBottom: '5px',
                        fontSize: '16px'
                      }}>
                        {box.label}
                      </div>
                      <div style={{ 
                        color: '#666', 
                        fontSize: '14px' 
                      }}>
                        {box.description}
                      </div>
                    </div>
                    <label 
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        cursor: 'pointer',
                        marginLeft: '20px'
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={displayOptions[box.key]}
                        onChange={() => handleToggle(box.key)}
                        style={{
                          width: '24px',
                          height: '24px',
                          cursor: 'pointer',
                          marginRight: '10px'
                        }}
                      />
                      <span style={{ 
                        fontWeight: displayOptions[box.key] ? 'bold' : 'normal',
                        color: displayOptions[box.key] ? '#2563eb' : '#666'
                      }}>
                        {displayOptions[box.key] ? 'Visible' : 'Hidden'}
                      </span>
                    </label>
                  </div>
                  
                  {/* Zmanim selection - show when Zmanim is enabled */}
                  {box.key === 'showZmanim' && displayOptions.showZmanim && (
                    <div style={{ 
                      paddingTop: '15px', 
                      borderTop: '1px solid rgba(0,0,0,0.1)'
                    }}>
                      <div style={{ fontWeight: 'bold', marginBottom: '10px', fontSize: '14px' }}>
                        Select which zmanim to display:
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '10px' }}>
                        {Object.values(ZmanEnum).map((zmanKey) => (
                          <label 
                            key={zmanKey}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              cursor: 'pointer',
                              padding: '8px',
                              backgroundColor: displayOptions.selectedZmanim?.includes(zmanKey) ? '#e0f2fe' : 'white',
                              borderRadius: '4px',
                              border: '1px solid #d1d5db',
                              transition: 'background-color 0.2s'
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={displayOptions.selectedZmanim?.includes(zmanKey) || false}
                              onChange={() => handleZmanimToggle(zmanKey)}
                              style={{
                                width: '18px',
                                height: '18px',
                                cursor: 'pointer',
                                marginRight: '8px'
                              }}
                            />
                            <span style={{ fontSize: '14px' }}>
                              {titleMapper[zmanKey] || zmanKey}
                            </span>
                          </label>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Daily Studies selection - show when Daily Studies is enabled */}
                  {box.key === 'showDailyStudies' && displayOptions.showDailyStudies && (
                    <div style={{ 
                      paddingTop: '15px', 
                      borderTop: '1px solid rgba(0,0,0,0.1)'
                    }}>
                      <div style={{ fontWeight: 'bold', marginBottom: '10px', fontSize: '14px' }}>
                        Select which studies to display:
                      </div>
                      <div style={{ display: 'grid', gap: '10px' }}>
                        {[
                          { key: 'onePerek', label: 'פרק אחד (1 Perek)' },
                          { key: 'threePerokim', label: 'שלושה פרקים (3 Perokim)' },
                          { key: 'tanya', label: 'תניא (Tanya)' }
                        ].map((study) => (
                          <label 
                            key={study.key}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              cursor: 'pointer',
                              padding: '8px',
                              backgroundColor: displayOptions.selectedDailyStudies?.includes(study.key) ? '#e0f2fe' : 'white',
                              borderRadius: '4px',
                              border: '1px solid #d1d5db',
                              transition: 'background-color 0.2s'
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={displayOptions.selectedDailyStudies?.includes(study.key) || false}
                              onChange={() => handleDailyStudyToggle(study.key)}
                              style={{
                                width: '18px',
                                height: '18px',
                                cursor: 'pointer',
                                marginRight: '8px'
                              }}
                            />
                            <span style={{ fontSize: '14px' }}>
                              {study.label}
                            </span>
                          </label>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Progress bar toggle - show when Community Updates is enabled */}
                  {box.key === 'showCommunityUpdates' && displayOptions.showCommunityUpdates && (
                    <div style={{ 
                      paddingTop: '15px', 
                      borderTop: '1px solid rgba(0,0,0,0.1)'
                    }}>
                      <label 
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          cursor: 'pointer'
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={displayOptions.showProgressBar !== false}
                          onChange={() => handleToggle('showProgressBar')}
                          style={{
                            width: '18px',
                            height: '18px',
                            cursor: 'pointer',
                            marginRight: '8px'
                          }}
                        />
                        <span style={{ fontSize: '14px', fontWeight: 'bold' }}>
                          Show progress bar in donation widget
                        </span>
                      </label>
                    </div>
                  )}

                  {/* Custom HTML editor - show when Custom HTML Box is enabled */}
                  {box.key === 'showCustomHtml' && displayOptions.showCustomHtml && (
                    <div style={{ 
                      paddingTop: '15px', 
                      borderTop: '1px solid rgba(0,0,0,0.1)'
                    }}>
                      <div style={{ fontWeight: 'bold', marginBottom: '10px', fontSize: '14px' }}>
                        Custom HTML Content:
                      </div>
                      <textarea
                        value={displayOptions.customHtmlContent || ''}
                        onChange={(e) => handleCustomHtmlChange(e.target.value)}
                        placeholder="Enter your custom HTML here..."
                        style={{
                          width: '100%',
                          minHeight: '200px',
                          padding: '10px',
                          fontSize: '14px',
                          fontFamily: 'monospace',
                          border: '1px solid #d1d5db',
                          borderRadius: '4px',
                          resize: 'vertical',
                          boxSizing: 'border-box'
                        }}
                      />
                      <div style={{ 
                        marginTop: '8px', 
                        fontSize: '12px', 
                        color: '#666',
                        fontStyle: 'italic'
                      }}>
                        Note: HTML will be rendered as-is. Use with caution.
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}

        <div style={{ 
          marginTop: '40px', 
          paddingTop: '20px', 
          borderTop: '2px solid #ddd',
          display: 'flex',
          alignItems: 'center',
          gap: '15px'
        }}>
          <button
            onClick={handleSave}
            disabled={saving}
            style={{
              padding: '12px 24px',
              fontSize: '16px',
              fontWeight: 'bold',
              backgroundColor: '#2563eb',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: saving ? 'not-allowed' : 'pointer',
              opacity: saving ? 0.6 : 1,
              transition: 'opacity 0.2s'
            }}
          >
            {saving ? 'Saving...' : 'Save Settings'}
          </button>

          {saveSuccess && (
            <div style={{ 
              color: '#059669', 
              fontWeight: 'bold',
              padding: '8px 16px',
              backgroundColor: '#d1fae5',
              borderRadius: '6px'
            }}>
              ✓ Settings saved successfully!
            </div>
          )}

          {saveError && (
            <div style={{ 
              color: '#dc2626', 
              fontWeight: 'bold',
              padding: '8px 16px',
              backgroundColor: '#fee2e2',
              borderRadius: '6px'
            }}>
              {saveError}
            </div>
          )}
        </div>
    </div>
  );
};

export default KioskSettings;

